<template>
  <div class="sales-rep-performance-table-root">
  <div class="table-responsive sales-rep-dashboard-table-wrap sales-rep-table-scroll">
    <table class="table table-bordered sales-rep-dashboard-table mb-0">
      <thead>
        <tr>
          <th rowspan="2" class="sales-rep-period-label">Period</th>
          <th colspan="6" class="text-center">Gross</th>
          <th colspan="6" class="text-center sales-rep-net-col">NET</th>
        </tr>
        <tr>
          <th class="text-end">Cust</th>
          <th class="text-end">Rev</th>
          <th class="text-end">Cancelled</th>
          <th class="text-end">Issue</th>
          <th class="text-end">Processing</th>
          <th class="text-end">LIVE</th>
          <th class="text-end sales-rep-net-col">Rev</th>
          <th class="text-end sales-rep-net-col">Cust</th>
          <th class="text-end sales-rep-net-col">Meters</th>
          <th class="text-end sales-rep-net-col">Uplift</th>
          <th class="text-end sales-rep-net-col">Term</th>
          <th class="text-end sales-rep-net-col">Avg Deal Value</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!standardRows.length && !customRow">
          <td colspan="13" class="text-center text-muted py-4">No performance data available</td>
        </tr>
        <tr v-for="row in standardRows" :key="row.period_key">
          <td class="sales-rep-period-label" :title="periodTooltip(row)">
            {{ row.period_label }}
          </td>
          <td class="text-end">{{ formatInteger(row.gross?.cust) }}</td>
          <td class="text-end">{{ formatCurrency(row.gross?.rev) }}</td>
          <td class="text-end">
            <bucket-cell :bucket="row.gross?.cancelled" />
          </td>
          <td class="text-end">
            <bucket-cell :bucket="row.gross?.issue" />
          </td>
          <td class="text-end">
            <bucket-cell :bucket="row.gross?.processing" />
          </td>
          <td class="text-end">
            <bucket-cell :bucket="row.gross?.live" />
          </td>
          <td class="text-end sales-rep-net-col">{{ formatCurrency(row.net?.rev) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatInteger(row.net?.cust) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatInteger(row.net?.meters) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatDecimal(row.net?.uplift, 2) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatDecimal(row.net?.term, 1) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatAverageContractValue(row.net?.rev, row.net?.meters) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
  <div class="sales-rep-custom-filters-bar">
    <span class="sales-rep-period-label me-2">CUSTOM</span>
    <div class="d-flex flex-wrap align-items-center gap-2">
      <div class="icon-form sales-rep-date-field">
        <date-picker
          textInput
          :model-value="customDateFrom"
          class="datetimepicker form-control"
          placeholder="From"
          :editable="true"
          :clearable="true"
          :input-format="dateFormat"
          @update:model-value="$emit('update:customDateFrom', $event)"
        />
        <span class="form-icon"><i class="ti ti-calendar-check"></i></span>
      </div>
      <div class="icon-form sales-rep-date-field">
        <date-picker
          textInput
          :model-value="customDateTo"
          class="datetimepicker form-control"
          placeholder="To"
          :editable="true"
          :clearable="true"
          :input-format="dateFormat"
          @update:model-value="$emit('update:customDateTo', $event)"
        />
        <span class="form-icon"><i class="ti ti-calendar-check"></i></span>
      </div>
      <button type="button" class="btn btn-primary btn-sm" :disabled="isLoading" @click="$emit('apply-custom')">
        Apply
      </button>
    </div>
  </div>
  <div v-if="customRow" class="table-responsive sales-rep-dashboard-table-wrap sales-rep-table-scroll">
    <table class="table table-bordered sales-rep-dashboard-table mb-0">
      <tbody>
        <tr :key="customRow.period_key">
          <td class="sales-rep-period-label" :title="periodTooltip(customRow)">
            {{ customRow.period_label }}
          </td>
          <td class="text-end">{{ formatInteger(customRow.gross?.cust) }}</td>
          <td class="text-end">{{ formatCurrency(customRow.gross?.rev) }}</td>
          <td class="text-end">
            <bucket-cell :bucket="customRow.gross?.cancelled" />
          </td>
          <td class="text-end">
            <bucket-cell :bucket="customRow.gross?.issue" />
          </td>
          <td class="text-end">
            <bucket-cell :bucket="customRow.gross?.processing" />
          </td>
          <td class="text-end">
            <bucket-cell :bucket="customRow.gross?.live" />
          </td>
          <td class="text-end sales-rep-net-col">{{ formatCurrency(customRow.net?.rev) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatInteger(customRow.net?.cust) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatInteger(customRow.net?.meters) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatDecimal(customRow.net?.uplift, 2) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatDecimal(customRow.net?.term, 1) }}</td>
          <td class="text-end sales-rep-net-col">{{ formatAverageContractValue(customRow.net?.rev, customRow.net?.meters) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
  </div>
</template>

<style scoped lang="scss">
:deep(.v3dp__popout) {
  z-index: 1060 !important;
}
</style>

<script>
import { computed, defineComponent, h } from 'vue';

const BucketCell = defineComponent({
  name: 'BucketCell',
  props: {
    bucket: { type: Object, default: null },
  },
  setup(props) {
    const formatInteger = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? Math.round(n).toLocaleString('en-GB') : '0';
    };
    const formatCurrency = (value, currency = 'GBP') => {
      const n = Number(value);
      if (!Number.isFinite(n)) return currency === 'GBP' ? '£0.00' : '0.00';
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    };
    return () => {
      const count = formatInteger(props.bucket?.count);
      const rev = formatCurrency(props.bucket?.rev);
      return h('div', { class: 'sales-rep-bucket-cell' }, [
        h('span', { class: 'sales-rep-bucket-count' }, count),
        h('span', { class: 'sales-rep-bucket-rev' }, rev),
      ]);
    };
  },
});

export default {
  name: 'SalesRepPerformanceTable',
  components: { BucketCell },
  props: {
    rows: { type: Array, default: () => [] },
    customDateFrom: { type: [Date, String, null], default: null },
    customDateTo: { type: [Date, String, null], default: null },
    isLoading: { type: Boolean, default: false },
  },
  emits: ['update:customDateFrom', 'update:customDateTo', 'apply-custom'],
  setup(props) {
    const dateFormat = 'dd/MM/yyyy';

    const standardRows = computed(() =>
      (props.rows || []).filter((r) => r.period_key !== 'custom')
    );

    const customRow = computed(() =>
      (props.rows || []).find((r) => r.period_key === 'custom') || null
    );

    const formatInteger = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? Math.round(n).toLocaleString('en-GB') : '0';
    };

    const formatCurrency = (value, currency = 'GBP') => {
      const n = Number(value);
      if (!Number.isFinite(n)) return '£0.00';
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    };

    const formatDecimal = (value, digits = 2) => {
      const n = Number(value);
      return Number.isFinite(n) ? n.toFixed(digits) : (0).toFixed(digits);
    };

    const formatAverageContractValue = (rev, meters) => {
      const r = Number(rev);
      const m = Number(meters);
      if (m > 0 && Number.isFinite(r)) {
        return formatCurrency(r / m);
      }
      return formatCurrency(0);
    };

    const periodTooltip = (row) => {
      if (!row?.date_from || !row?.date_to) return '';
      return `${row.date_from} – ${row.date_to}`;
    };

    return {
      dateFormat,
      standardRows,
      customRow,
      formatInteger,
      formatCurrency,
      formatDecimal,
      formatAverageContractValue,
      periodTooltip,
    };
  },
};
</script>
